import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  User, CheckCircle, Loader2, Upload, FileText, Camera, IndianRupee, AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const UPI_QR_URL = "https://customer-assets.emergentagent.com/job_337dba5c-8d5d-4e7d-adac-9993bcb0c3e3/artifacts/uxuej4sd_WhatsApp%20Image%202026-02-15%20at%2013.14.34.jpeg";

const PSGuestVolunteerCompletePage = () => {
  const { token } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  const [email, setEmail] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [paymentMode, setPaymentMode] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentProof, setPaymentProof] = useState(null);
  const profileRef = useRef(null);
  const paymentRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/ps/guest-volunteer/complete/${token}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? 'Link not found or expired' : 'Already completed');
        return r.json();
      })
      .then(d => { setRecord(d); setEmail(d.email || ''); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const isPaid = record && record.fee > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profilePhoto && !record.profilePhotoUrl) { toast.error('Profile Photo is required / फोटो अनिवार्य है'); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.error('Valid email is required / ईमेल आवश्यक है'); return; }
    if (isPaid) {
      if (!paymentMode) { toast.error('Please select payment mode'); return; }
      if (!transactionId.trim()) { toast.error('Transaction ID is required'); return; }
      if (!paymentProof && !record.paymentProofUrl) { toast.error('Payment screenshot is required'); return; }
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      if (email) fd.append('email', email);
      if (profilePhoto) fd.append('profilePhoto', profilePhoto);
      if (paymentMode) fd.append('paymentMode', paymentMode);
      if (transactionId) fd.append('transactionId', transactionId);
      if (paymentProof) fd.append('paymentProof', paymentProof);

      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/complete/${token}`, {
        method: 'POST', body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setSubmitted(true);
        toast.success('Registration completed!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Submission failed');
      }
    } catch { toast.error('Network error'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="max-w-md w-full p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Link Invalid</h2>
        <p className="text-gray-600">{error}</p>
      </Card>
    </div>
  );

  if (submitted && result) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
        <Card className="max-w-lg w-full p-8 text-center" data-testid="gv-complete-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Complete!</h2>
          <p className="text-gray-600 mb-4">पंजीकरण पूर्ण!</p>
          <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2">
            <p className="text-sm"><span className="text-gray-500">Name:</span> <strong>{result.fullName}</strong></p>
            <p className="text-sm"><span className="text-gray-500">Member No:</span> <strong className="font-mono">{result.memberNumber}</strong></p>
            <p className="text-sm"><span className="text-gray-500">Category:</span> <strong>{result.category}</strong></p>
            <p className="text-sm"><span className="text-gray-500">Status:</span> <strong className="text-yellow-600">Pending Approval</strong></p>
          </div>
          <p className="text-sm text-gray-500 mt-4">Your ID Card will be generated once approved by admin.</p>
        </Card>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="gradient-orange py-10">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Parichay Sangam 2026</h1>
          <p className="text-lg text-white/90">Complete Your Registration / पंजीकरण पूर्ण करें</p>
        </div>
      </section>

      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 py-8">
        {/* Pre-filled Info (read-only) */}
        <Card className="p-6 mb-6" data-testid="gv-prefilled-info">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-primary" /> Your Details / आपका विवरण
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ['Member No.', record.memberNumber],
              ['Name / नाम', record.fullName],
              ["Father's / Husband's Name", record.fatherOrHusbandName],
              ['Nukh / नुख', record.nukh],
              ['Place / स्थान', record.currentPlace],
              ['WhatsApp', record.whatsapp],
              ['Category / श्रेणी', record.category],
              ['Fee / शुल्क', record.fee > 0 ? `₹${record.fee.toLocaleString()}` : 'Free / निःशुल्क'],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="font-medium text-gray-900">{value}</p>
              </div>
            ))}
            {record.roomNumber && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Room No.</p>
                <p className="font-medium text-gray-900">{record.roomNumber}</p>
              </div>
            )}
            {record.seatNumber && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Seat No.</p>
                <p className="font-medium text-gray-900">{record.seatNumber}</p>
              </div>
            )}
          </div>

          <div className="mt-4">
            <Label className="text-sm">Email Address / ईमेल <span className="text-destructive">*</span></Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" className="mt-1 max-w-sm" data-testid="gv-complete-email" />
          </div>
        </Card>

        {/* Photo Upload */}
        <Card className="p-6 mb-6" data-testid="gv-complete-photo-section">
          <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" /> Profile Photo / फोटो <span className="text-destructive">*</span>
          </h3>
          <p className="text-xs text-gray-500 mb-3">Clear, front-facing photo for ID Card / ID Card के लिए स्पष्ट फोटो</p>
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => profileRef.current?.click()}
              className={`flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-colors ${
                profilePhoto ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-primary'
              }`} data-testid="gv-complete-photo-upload">
              {profilePhoto ? (
                <><CheckCircle className="w-4 h-4 text-green-600" /><span className="text-green-700 font-medium truncate max-w-[200px]">{profilePhoto.name}</span></>
              ) : (
                <><Upload className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Upload Photo (JPG, PNG, WEBP, max 5MB)</span></>
              )}
            </button>
            <input ref={profileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
              onChange={(e) => { const f = e.target.files[0]; if (f) { if (f.size > 5*1024*1024) { toast.error('Max 5MB'); return; } setProfilePhoto(f); setProfilePreview(URL.createObjectURL(f)); }}} />
            {profilePreview && <img src={profilePreview} alt="Preview" className="w-16 h-16 object-cover rounded-lg border" />}
          </div>
        </Card>

        {/* Payment (for paid categories) */}
        {isPaid && (
          <Card className="p-6 mb-6 border-orange-200 bg-orange-50/30" data-testid="gv-complete-payment-section">
            <h3 className="text-lg font-bold text-orange-800 mb-1 flex items-center gap-2">
              <IndianRupee className="w-5 h-5" /> Payment: {'\u20B9'}{record.fee.toLocaleString()}
            </h3>
            <p className="text-xs text-orange-700 mb-4">Please complete payment before submitting / सबमिट करने से पहले भुगतान करें</p>

            <div className="flex gap-3 mb-3">
              {['UPI', 'Bank Transfer'].map(mode => (
                <Button key={mode} type="button" variant={paymentMode === mode ? 'default' : 'outline'} size="sm"
                  onClick={() => setPaymentMode(mode)}
                  className={paymentMode === mode ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  data-testid={`gv-complete-payment-${mode.toLowerCase().replace(' ', '-')}`}>
                  {mode}
                </Button>
              ))}
            </div>

            {paymentMode === 'UPI' && (
              <div className="bg-white p-4 rounded-lg border border-blue-200 mb-3">
                <h5 className="font-semibold text-blue-800 mb-3 text-center">UPI से भुगतान करें / Pay via UPI</h5>
                <div className="flex flex-col items-center">
                  <img src={UPI_QR_URL} alt="UPI QR Code" className="max-w-[200px] w-full h-auto rounded-lg shadow-md mb-3" />
                  <p className="text-sm text-blue-700 font-medium">UPI ID: boim-661059350985@boi</p>
                </div>
              </div>
            )}

            {paymentMode === 'Bank Transfer' && (
              <div className="bg-white p-4 rounded-lg border border-green-200 mb-3">
                <h5 className="font-semibold text-green-800 mb-2">Bank Details / बैंक विवरण</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div><span className="font-medium text-green-700">Account Name:</span><p>BRAHMAKSHATRIYA MARVADI KHATRI ASSOCIATION</p></div>
                  <div><span className="font-medium text-green-700">Bank:</span><p>BANK OF INDIA</p></div>
                  <div><span className="font-medium text-green-700">Account No:</span><p className="font-mono select-all">661020110000985</p></div>
                  <div><span className="font-medium text-green-700">IFSC:</span><p className="font-mono select-all">BKID0006610</p></div>
                </div>
              </div>
            )}

            {paymentMode && (
              <>
                <div className="mb-3">
                  <Label className="text-sm">Transaction ID / UTR Number <span className="text-destructive">*</span></Label>
                  <Input value={transactionId} onChange={(e) => setTransactionId(e.target.value)} placeholder="Enter UTR / Transaction Reference" className="mt-1" data-testid="gv-complete-txn-id" />
                </div>
                <div>
                  <Label className="text-sm">Payment Screenshot <span className="text-destructive">*</span></Label>
                  <button type="button" onClick={() => paymentRef.current?.click()}
                    className={`flex items-center gap-2 w-full px-4 py-3 border-2 border-dashed rounded-lg text-sm mt-1 transition-colors ${
                      paymentProof ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-orange-400'
                    }`} data-testid="gv-complete-payment-proof">
                    {paymentProof ? (
                      <><CheckCircle className="w-4 h-4 text-green-600" /><span className="text-green-700 font-medium">{paymentProof.name}</span></>
                    ) : (
                      <><FileText className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Upload payment screenshot</span></>
                    )}
                  </button>
                  <input ref={paymentRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                    onChange={(e) => { if (e.target.files[0]) setPaymentProof(e.target.files[0]); }} />
                </div>
              </>
            )}
          </Card>
        )}

        <Button type="submit" disabled={submitting} className="w-full py-6 text-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-50" data-testid="gv-complete-submit">
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Submitting...</> : 'Submit / सबमिट करें'}
        </Button>
      </form>
    </div>
  );
};

export default PSGuestVolunteerCompletePage;
